/**
 * The 'enableTest' field of an Action definitions may be set to one of these, or to an array of them for multiple tests.
 * @module core/enablers
 */

import {getViewCollection, getViewModel} from './utils';
import {userSource as userSources} from '@netx/core-user/properties/user';
import {isMobile as checkIsMobile} from './underscore-netx';
import {mediaServicesAvailable} from '@netx/core-share-media-service/enablers';
import {assetProjectEnabled} from '@netx/core-asset-project/enablers';
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import {userProfile} from '@netx/core-user/collections/singletons/userProfile';

/**
 * Clips cannot be emailed currently
 * @function
 * @returns {boolean}
 */
export const canEmail = (v) => {
	if (v.data && v.data.assetSet) {
		return v.data.assetSet.get('canEmail') !== false;
	}
	return true;
};

/**
 * Can user logout
 * @function
 * @return {boolean}
 */
export const canLogout = (v) => {
	const userSource = userProfile.get('userSource');
	// If user source is INTERNAL or SAML
	return userSource === userSources.INTERNAL || userSource === userSources.SAML;
};

// TODO: We should not reference Netx.[module] (other than user) for enable tests
// DAM-11555 - currently used only for video publishing
/**
 * @function
 * @returns {boolean}
 */
export const mediaServicesEnabled = (v) => {
	if (mediaServicesAvailable()) {
		var mediaServices = Netx.share.mediaServices,
			brightCove = mediaServices.find({name: 'Brightcove'}),
			youTube = mediaServices.find({name: 'YouTube'});

		return (
			(youTube && youTube.get('authenticated')) ||
			(brightCove && brightCove.get('authenticated'))
		);
	}
	return false;
};

// collection has at least one model
/**
 * @function
 * @returns {boolean}
 */
export const collectionNotEmpty = (v) => {
	const collection = getViewCollection(v);
	if (!collection) return false;

	// Sometimes we update the pager before we get models in a collection
	const pager = collection.pager;
	if (!pager) return collection.length > 0;

	return pager.get('itemTotal') > 0;
};

/**
 * @function
 * @returns {boolean}
 */
export const canUseGridEditor = (v) => {
	return true;
};

/**
 * @function
 * @returns {boolean}
 */
export const allowsDelete = (v) => {
	if (v.model && _.isFunction(v.model.allows)) {
		return v.model.allows('delete');
	} else if (v.collection && _.isFunction(v.collection.allows)) {
		return v.collectionl.allows('delete');
	}
	return false;
};

// For the case when you want to move an asset out of/into a category,
// for which you need modify permissions on both.  We can't know whether
// to expect an asset view or a category view here.  We have a few different
// patterns for inserting extra references, too, so here we perform an
// exhaustive search.
/**
 * @function
 * @returns {boolean}
 */
export const categoryAndAssetCanEdit = (view) => {
	// Best approach is if the view itself has both .asset and .category, being links to appropriate models.
	if (view.asset && view.category) {
		return view.asset.allows('modify') && view.category.allows('modify');
	}

	// view.model could be either an asset model or a catgegory model, but either way this has to pass:
	if (!view.model || !view.model.allows || !view.model.allows('modify')) {
		return false;
	}

	if (view.model.constructor.__name__ === 'Category') {
		if (view.asset) {
			// If a category view has an asset model as .asset, this will work.
			return view.asset.allows('modify');
		} else {
			// NOTE: this view.model.get('asset') thing is kind of a hack, used to reference the original asset model in the asset:category:edit handler.
			// I'd rather we attached that directly to the view (as above), because its meaning can be misconstrued otherwise.
			// TODO: fix that up.
			return (
				view.model.get('asset') && view.model.get('asset').allows('modify')
			);
		}
	}

	if (view.model.constructor.__name__ === 'Asset') {
		// if an asset view has a category model as .category, this will work.
		if (view.category) {
			return view.category.allows('modify');
		} else {
			// if the current category just so happens to be our asset's "main" category, we're in luck and can test.
			view.model.get('categoryid') == Netx.category.current.id &&
				Netx.category.current.allows('modify');
		}
	}

	// Couldn't find a test, alas.
	return false;
};

/**
 * Have we set `header.helpUrl`?
 * @function
 * @returns {boolean}
 */
export const helpUrl = (v) => {
	return !_.isUndefined(getPreference('header.helpUrl'));
};

/**
 * @function
 * @returns {boolean}
 */
export const isMobile = () => {
	return checkIsMobile(false);
};

/**
 * @function
 * @returns {boolean}
 */
export const notMobile = () => {
	return !isMobile();
};

/**
 * @function
 * @returns {boolean}
 */
export const readOnly = (v) => {
	const model = getViewModel(v);
	return !model.allows('write');
};

/**
 * Asset review enabled
 * @function
 * @returns {boolean}
 */
export const reviewEnabled = (v) => {
	// DAM-14088
	if (checkIsMobile()) return false;

	return assetProjectEnabled();
};
